import { ArenaOpsUsdtSchema } from "./arena-ops-filters.dto";
import { z } from "zod";

/**
 * The four parts the liability decomposes into, in the order the ring draws
 * them and the legend lists them. An enum rather than a free string: a segment
 * the UI has no colour for would otherwise render as an unlabelled arc.
 */
export const ArenaOpsCoverageSegmentKeySchema = z.enum(["staff", "real", "pendingWithdraw", "bots"]);
export type ArenaOpsCoverageSegmentKey = z.infer<typeof ArenaOpsCoverageSegmentKeySchema>;

export const ArenaOpsCoverageSegmentSchema = z.object({
  key: ArenaOpsCoverageSegmentKeySchema,
  label: z.string(),
  amountUsdt: ArenaOpsUsdtSchema,
});
export type ArenaOpsCoverageSegment = z.infer<typeof ArenaOpsCoverageSegmentSchema>;

/**
 * O2 — the one panel in the dashboard that leaves the database: the ledger
 * liability against the on-chain treasury. Its own endpoint, its own timeout;
 * it must never share a request with a SQL panel.
 */
export const ArenaOpsOverviewCoverageRingsResponseSchema = z.object({
  ledgerLiabilityUsdt: ArenaOpsUsdtSchema,
  onChainTreasuryUsdt: ArenaOpsUsdtSchema,
  pendingWithdrawUsdt: ArenaOpsUsdtSchema,
  /**
   * treasury ÷ liability as a decimal string. **Under 1 means UNCOVERED** —
   * the vault holds less than it owes. `"Infinity"` when the liability is zero — a
   * defined limit, read by `parseRatio`, unlike the matchmaking rates' `""` for a rate
   * that does not exist. Never rendered as a number when the chain read fails: an absent
   * coverage figure must read as unknown, not as 0%.
   */
  coverageRatio: z.string(),
  /**
   * The liability broken into the parts the outer ring draws, ARRAY-ordered so
   * the ring and its legend iterate one source and can never disagree about
   * order. Their amounts sum to `ledgerLiabilityUsdt` exactly — arcs that do
   * not close are a bug, not a rounding artefact.
   */
  segments: z.array(ArenaOpsCoverageSegmentSchema),
  chainId: z.number().int(),
  /** Every Arena withdraw vault, newest first: older vaults still owe their unclaimed withdrawals. */
  treasuryAddresses: z.array(z.string()),
  chainReadAt: z.iso.datetime(),
});
export type ArenaOpsOverviewCoverageRingsResponse = z.infer<typeof ArenaOpsOverviewCoverageRingsResponseSchema>;
